import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Popover,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  VStack,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import Decimal from "decimal.js";
import { FaCheckCircle } from "react-icons/fa";
import {
  ProductionCapacityBenchmark,
  EquipmentType,
  ProductionLineType,
  UniqueProcessType,
  PagePermissions,
  // BenchmarkStateData,
  ProjectAdditionalInfo,
} from "../../contexts/types";
import {
  Messages,
} from '../../contexts/constants';
import { useTranslation } from 'react-i18next';
import BenchmarkInputPopover from "./BenchmarkInputPopover";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ProjectContext } from "./InvestmentScreeningDetail";

/**
 * モーダルのプロパティ
 */
interface Props {
  /** モーダルの表示/非表示 */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
  /** プロジェクトID */
  projectId: string;
  /** 既存のベンチマークデータ */
  benchmarkData: ProductionCapacityBenchmark[];
  /** ベンチマークデータ更新関数 */
  onUpdate: (data: ProductionCapacityBenchmark[]) => void;
  /** 設備データ（増産設備のみ） */
  equipmentData: EquipmentType[];
  /** 生産ラインデータ */
  productionLineData: ProductionLineType[];
  /** プロセスコードデータ */
  processCodeData: UniqueProcessType[];
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** 通貨名（ローカル通貨） */
  currencyName?: string;
  /** PartNoデータ（quantityAndPrice、displayPartNo含む） */
  partNoData?: { partNo: string; displayPartNo?: string; quantityAndPrice?: string | null }[];
}

/**
 * 過去ベストベンチマークモーダル
 */
const ProductionCapacityBenchmarkModal: React.FC<Props> = ({
  isOpen,
  onClose,
  projectId,
  equipmentData,
  onUpdate,
  productionLineData,
  processCodeData,
  pagePermissions,
  currencyName,
  partNoData,
}) => {
  const { t } = useTranslation();
  const pb = "features.InvestmentScreening.ProductionCapacityBenchmarkModal";
  /** タイトル */
  const TITLE = t(`${pb}.title`);
  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** 振り返りモード */
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;
  /** トースト表示用 */
  const toast = useToast();
  /**  保存モーダル保存ボタン活性制御 */
  const [isSaving, setIsSaving] = useState(false);
  /** 入力モード: "equipment" | "line" */
  const [inputType, setInputType] = useState<"equipment" | "line">("equipment");
  /** プロジェクト追加情報 */
  const [projectAdditionalInfo, setProjectAdditionalInfo] = useState<ProjectAdditionalInfo>({});
  /** ローカルベンチマークデータマップ */
  const [benchmarkMap, setBenchmarkMap] = useState<Map<string, ProductionCapacityBenchmark>>(
    new Map()
  );
  /**プロジェクトID */
  const ProjectId = projectId;
  /** PartNo別財務データマップ: { partNo → { unitPrice, materialCostPerUnit, quantity } } */
  const [partNoFinancialMap, setPartNoFinancialMap] = useState<Map<string, { unitPrice: number; materialCostPerUnit: number; quantity: number }>>(new Map());

  /**
   * モーダル表示切替時
   */
  useEffect(() => {
    if (!isOpen) {
      // 閉じた時
      return;
    }

    const fetchData = async () => {
      // プロジェクト追加情報取得
      const projectAdditionalInfo: ProjectAdditionalInfo = await getProjectAdditionalInfoById(projectId);
      setProjectAdditionalInfo(projectAdditionalInfo);

      try {
        if (projectAdditionalInfo.benchmarkData) {
          // JSONパース
          const benchmarkData: ProductionCapacityBenchmark[] = JSON.parse(projectAdditionalInfo.benchmarkData);

          // mapデータ変換
          const benchmarkMap = convertBenchmarkMap(benchmarkData, equipmentData);
          setBenchmarkMap(benchmarkMap);

          if (benchmarkData.length > 0) {
            // 入力タイプはすべて同じ想定なので先頭の入力タイプを設定
            setInputType(benchmarkData[0].inputType);
          }

        } else {
          const benchmarkMap = convertBenchmarkMap([], []);
          setBenchmarkMap(benchmarkMap);
        }
      } catch (error) {
        toast({
          title: TITLE,
          description: t(`${pb}.message.fetchDisplayDataFailed`),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`${TITLE} 表示データ取得失敗`, [error, projectAdditionalInfo]);
      }

      // PartNo別財務データ構築（売上インパクト用）
      try {
        await buildPartNoFinancialMap();
      } catch (error) {
        console.error(`${TITLE} PartNo財務データ構築失敗`, error);
      }
    };

    // プロジェクト追加情報取得実行
    fetchData();

  }, [isOpen]);

  /**
   * ベンチマークデータをMapデータへ変換
   * @param benchmarkData ベンチマークデータ
   * @returns ベンチマークデータMap
   */
  const convertBenchmarkMap = (
    benchmarkData: ProductionCapacityBenchmark[],
    equipmentData: EquipmentType[]
  ): Map<string, ProductionCapacityBenchmark> => {
    const equipmentIds = new Set(equipmentData.map(e => e.equipmentId)); // equipmentId の集合を作る
    const result = new Map<string, ProductionCapacityBenchmark>();

    const displayLineId = productionLineData.length > 0 ? productionLineData[0].productionLineId : undefined;

    benchmarkData.forEach(item => {
      if (item.inputType === "line" && item.productionLineId && item.productionLineId === displayLineId) {
        // ライン型は画面表示対象の productionLineId と一致するものだけ残す
        result.set(item.productionLineId, item);
      } if (item.inputType === "line" && item.productionLineId && displayLineId && Array.isArray(productionLineData) && productionLineData.length === 1) {
        // コピー案件でitem.productionLineIdがコピー元のproductionLineIdで保存されている場合
        result.set(displayLineId, {
          ...item,
          productionLineId: displayLineId,
        });
      } else if (item.equipmentId && equipmentIds.has(item.equipmentId)) {
        // 設備型は equipmentId があり equipmentData に存在する場合のみ追加
        result.set(item.equipmentId, item);
      }
    });

    return result;
  };
  // const convertBenchmarkMap = (benchmarkData: ProductionCapacityBenchmark[]) => {
  //   const result = new Map<string, ProductionCapacityBenchmark>();
  //   benchmarkData.forEach((item) => {
  //     const key = item.equipmentId || item.productionLineId || "";
  //     if (key) {
  //       result.set(key, item);
  //     }
  //   });
  //   // setBenchmarkMap(map);
  //   return result;
  // }

  /**
   * ベンチマークデータ保存ハンドラー
   */
  const handleSaveBenchmark = async (data: any): Promise<boolean> => {
    const key = data.equipmentId || data.productionLineId || "";

    if (isSaving) {
      return false;
    }

    setIsSaving(true);

    if (!key) {
      // IDが特定できない場合
      return false;
    }

    // stateのMapを直接書き換えると、保存が失敗した場合でも未保存データが残り続け、
    // モーダルを閉じた際に親へ「保存済み」として渡ってしまうため、コピーを対象に更新する
    const localBenchmarkMap = new Map(benchmarkMap);

    // 更新用データ
    localBenchmarkMap.set(key, data);

    // 配列に変換
    let benchmarkList = Array.from(localBenchmarkMap.values());

    // 入力タイプで選択中のもののみにする
    benchmarkList = benchmarkList.filter(b => b.inputType === inputType);

    if (inputType === "line") {
      // productionLineId ごとに後勝ちで1件に絞る（重複排除）
      const lineMap = new Map<string, ProductionCapacityBenchmark>();
      benchmarkList.forEach(item => lineMap.set(item.productionLineId || "", item));
      benchmarkList = Array.from(lineMap.values());
    }

    if (inputType === "equipment") {
      // 設備情報取得 ⇒ 最新が必要なら取得
      // const equipmentData: EquipmentType[] = await getEquipmentByProjectId(projectId);

      // 設備IDで突き合わせて存在するだけにする(フィルター)
      benchmarkList.filter(b => equipmentData.some(e => e.equipmentId === b.equipmentId));
    }

    // ベンチマークデータ登録/更新
    const result = await putBenchmark(benchmarkList);

    if (result) {
      // 遅延実行されるので、別途mapを更新する必要がある
      setBenchmarkMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(key, data);
        return newMap;
      });
    }
    setIsSaving(false);
    return result;
  };

  /**
   * PartNo別財務データマップを構築
   * partNoData（quantityAndPrice）から単価を、getMaterialCostから材料費を取得
   */
  const buildPartNoFinancialMap = async () => {
    const financialMap = new Map<string, { unitPrice: number; materialCostPerUnit: number; quantity: number }>();

    if (!partNoData || partNoData.length === 0) {
      setPartNoFinancialMap(financialMap);
      return;
    }

    // 1. quantityAndPriceから単価・販売数量を取得
    for (const pn of partNoData) {
      if (!pn.partNo) continue;
      let unitPrice = 0;
      let quantity = 0;
      if (pn.quantityAndPrice) {
        try {
          const parsed = JSON.parse(pn.quantityAndPrice);
          const years = parsed.years || [];
          if (years.length > 0 && parsed.data) {
            // 全年度の月別データから数量合計・加重平均単価を算出
            let totalSales = new Decimal(0);
            for (const year of years) {
              const yearData = parsed.data[year];
              if (yearData?.months) {
                const monthKeys = Object.keys(yearData.months);
                for (const mk of monthKeys) {
                  const mq = Number(yearData.months[mk]?.quantity || 0);
                  const mp = Number(yearData.months[mk]?.price || 0);
                  quantity += mq;
                  totalSales = totalSales.plus(new Decimal(mq).times(mp));
                }
              }
            }
            // 加重平均単価 = 総売上 / 総数量
            if (quantity > 0) {
              unitPrice = totalSales.div(quantity).toNumber();
            }
          }
        } catch (e) {
          console.warn(`quantityAndPrice パース失敗: ${pn.partNo}`, e);
        }
      }
      financialMap.set(pn.partNo, { unitPrice, materialCostPerUnit: 0, quantity });
    }

    // 2. materialCostからPartNo別材料費を取得
    const partNosToFetch = partNoData.filter(pn => pn.partNo).map(pn => pn.partNo);
    const materialCostResults = await Promise.all(
      partNosToFetch.map(partNo => fetchMaterialCost(projectId, partNo))
    );

    materialCostResults.forEach((result, idx) => {
      const partNo = partNosToFetch[idx];
      if (!result || !result.materialCost) return;

      try {
        // materialCost JSON: フラット配列 CBomDataWithMonthly[] (PartNo単位で取得済み)
        const parsed = JSON.parse(result.materialCost);
        const bomItems = Array.isArray(parsed) ? parsed : (parsed[partNo] || []);
        // 全BOMアイテムのInternalAdjustedAmountを合算 = 1個あたり材料費
        let totalMaterialCost = 0;
        for (const item of bomItems) {
          // 月別データがある場合は最初の月を使用、なければルートレベル
          if (item.monthlyData) {
            const monthKeys = Object.keys(item.monthlyData);
            if (monthKeys.length > 0) {
              const firstMonthData = item.monthlyData[monthKeys[0]];
              totalMaterialCost += Number(firstMonthData?.InternalAdjustedAmount || 0);
              continue;
            }
          }
          totalMaterialCost += Number(item.InternalAdjustedAmount || 0);
        }

        const existing = financialMap.get(partNo);
        if (existing) {
          existing.materialCostPerUnit = totalMaterialCost;
        }
      } catch (e) {
        console.warn(`materialCost パース失敗: ${partNo}`, e);
      }
    });

    setPartNoFinancialMap(financialMap);
  };

  /**
   * PartNo別材料費取得
   */
  const fetchMaterialCost = async (projectId: string, partNo: string) => {
    try {
      const payload = {
        query: `
          query getMaterialCost($projectId: String, $partNo: String, $reviewDate: String) {
            getMaterialCost(projectId: $projectId, partNo: $partNo, reviewDate: $reviewDate) {
              projectId
              partNo
              materialCost
              materialCostWithInhouseProfit
            }
          }`,
        variables: { projectId, partNo, ...(reviewDate && { reviewDate }) }
      };
      const result = await WebAPI.request(payload);
      return result?.data?.getMaterialCost || null;
    } catch (error) {
      console.warn(`materialCost取得失敗: ${partNo}`, error);
      return null;
    }
  };

  /**
   * プロジェクト追加情報取得（ベストベンチマーク情報取得）
   * @param projectId プロジェクトID
   * @returns
   */
  const getProjectAdditionalInfoById = async (projectId: string) => {
    try {
      // AppSync
      const payload = {
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              benchmarkData
              updateDate
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      };
      // APIリクエスト実行
      const result = await WebAPI.request(payload);
      console.log({ "リクエスト完了": result });
      if (!result?.data?.getProjectAdditionalInfoById) {
        // リクエスト失敗
        toast({
          title: TITLE,
          description: t(`${pb}.message.fetchBenchmarkFailed`),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getProjectAdditionalInfoById. errors = `, result);
      }

      return result?.data?.getProjectAdditionalInfoById;

    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t(`${pb}.message.fetchBenchmarkFailed`),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return null;
    };
  };

  /**
   * ベンチマークデータ保存
   * @param benchmarkList ベンチマークデータ
   */
  const putBenchmark = async (benchmarkList: ProductionCapacityBenchmark[]) => {
    try {
      // 登録パラメータ
      const input = {
        projectId: projectId,
        benchmarkData: JSON.stringify(benchmarkList.map(({ projectId: _, ...rest }: any) => rest)),
        updateDate: projectAdditionalInfo?.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation putBenchmark($input: BenchmarkInput!) {
              putBenchmark(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`putBenchmark result = `, result);
      console.log({ "リクエスト完了": result });
      if (result && Object.keys(result).length > 0) {

        // リクエスト成功
        toast({
          title: t(`${pb}.toast.saveSuccess`),
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // データ再取得
        const projectAdditionalInfo: ProjectAdditionalInfo = await getProjectAdditionalInfoById(projectId);
        setProjectAdditionalInfo(projectAdditionalInfo);

        if (projectAdditionalInfo.benchmarkData) {
          try {
            // JSONパース
            const benchmarkData = JSON.parse(projectAdditionalInfo.benchmarkData);

            // mapデータ変換
            const benchmarkMap = convertBenchmarkMap(benchmarkData, equipmentData);
            setBenchmarkMap(benchmarkMap);
          } catch (error) {
            toast({
              title: TITLE,
              description: t(`${pb}.message.fetchDisplayDataFailed`),
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            console.error(`${TITLE} 表示データ取得失敗`, [error, projectAdditionalInfo]);
          }
        }

        return true;

      } else if (result?.data?.putBenchmark.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putBenchmark.`);
      } else {
        // リクエスト失敗
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putBenchmark. errors = `, result);
      }

    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('common.message.fetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    };

    return false;
  };
  /**設備ID */
  const [equipmentId, setEquipmentId] = useState<EquipmentType[]>([]);
  /**過去設備ID */
  const [pastEquipmentId, setPastEquipmentId] = useState<EquipmentType[]>([]);
  /**過去プロジェクトID */
  const [pastProjectId, setPastProjectId] = useState<string>("");
  /**過去プロジェクトの振り返り日（紐づけ時点の承認済み振り返り日。申請のみの場合はnull） */
  const [pastReviewDate, setPastReviewDate] = useState<string | null>(null);
  /**過去ラインID */
  const [pastProductionLineId, setPastProductionLineId] = useState<string>("");
  /**過去ライン */
  const [pastProductionLine, setPastProductionLine] = useState<string>("");
  /**過去BOM　C/T */
  const [pastUniqueProcess, setPastuniqueProcess] = useState<UniqueProcessType>();

  useEffect(() => {
    // モーダルを開いていない間は取得不要（非活性ボタンで開けないユーザーの無駄なアクセスを防ぐ）
    if (!isOpen) {
      return;
    }
    /**
     * プロジェクト追加情報取得（ベストベンチマーク情報取得）
     * @param projectId プロジェクトID
     * @returns
     */
    const getPastProjectId = async (Id: string) => {
      if (!Id) {
        return;
      }
      try {
        // 過去PJの処理時は紐づけ時点の承認済み振り返り日（pastReviewDate）を使う（現PJは画面のreviewDate）
        const effectiveReviewDate = Id === pastProjectId
          ? pastReviewDate
          : reviewDate;

        // AppSync
        const payload = {
          query: `
          query getProjectById($projectId: String, $useRDSData: Boolean, $reviewDate: String, $referencingProjectId: String) {
              getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
                projectId
                productionLineId
                pastProjectId
                pastReviewDate
              }
          }`,
          variables: {
            projectId: Id,
            useRDSData: true,
            ...(effectiveReviewDate && { reviewDate: effectiveReviewDate }),
            ...(Id === pastProjectId && { referencingProjectId: projectId })
          }
        };
        // APIリクエスト実行
        const result = await WebAPI.request(payload);

        if (!result?.data?.getProjectById) {
          // リクエスト失敗
          toast({
            title: TITLE,
            description: t(`${pb}.message.fetchPastProjectIdFailed`),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          console.error(`Failed to call API getProjectById. errors = `, result);
          return;
        }

        // pastProjectIDなら
        if (Id === pastProjectId) {
          // 過去ラインIdを取得
          setPastProductionLineId(result.data.getProjectById.productionLineId)
        } else {
          // 過去PJIdを取得
          setPastProjectId(result.data.getProjectById.pastProjectId)
          setPastReviewDate(result.data.getProjectById.pastReviewDate ?? null)
        }

      } catch (error) {
        console.error("データ取得失敗:", error);
        toast({
          title: TITLE,
          description: t(`${pb}.message.fetchEquipmentFailed`),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return null;
      };
    };
    getPastProjectId(projectId);
    getPastProjectId(pastProjectId);
  }, [isOpen, ProjectId, pastProjectId, pastReviewDate])


  useEffect(() => {
    // モーダルを開いていない間は取得不要（非活性ボタンで開けないユーザーの無駄なアクセスを防ぐ）
    if (!isOpen) {
      return;
    }
    /**
     * プロジェクト追加情報取得（ベストベンチマーク情報取得）
     * @param projectId プロジェクトID
     * @returns
     */
    const getEquipmentByProjectId = async (Id: string) => {
      if (!Id) {
        return;
      }
      try {
        // 過去PJの処理時は紐づけ時点の承認済み振り返り日（pastReviewDate）を使う（現PJは画面のreviewDate）
        const effectiveReviewDate = Id === pastProjectId
          ? pastReviewDate
          : reviewDate;

        // AppSync
        const payload = {
          query: `
          query fetchBenchmarkEquipmentData($Id: String, $reviewDate: String, $referencingProjectId: String) {
            getEquipmentByProjectId(projectId: $Id, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
              equipmentId
              equipmentName
              unitPrice
              quantity
              quantityUnit
              numberOfPeopleRequiredForEquipment
            }
            getUniqueProcessByProjectId(projectId: $Id, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
              uniqueProcessId
              uniqueProcessCycleTimeInSecondByPartNo
            }
          }`,
          variables: {
            Id,
            ...(effectiveReviewDate && { reviewDate: effectiveReviewDate }),
            ...(Id === pastProjectId && { referencingProjectId: projectId })
          }
        };
        // APIリクエスト実行
        const result = await WebAPI.request(payload);

        // pastProjectIDなら
        if (Id === pastProjectId) {
          setPastEquipmentId(result.data?.getEquipmentByProjectId ?? []);
          setPastuniqueProcess(result.data?.getUniqueProcessByProjectId);
        } else {
          setEquipmentId(result.data?.getEquipmentByProjectId ?? []);
        }
        if (!result?.data?.getEquipmentByProjectId) {
          // リクエスト失敗
          toast({
            title: TITLE,
            description: t(`${pb}.message.fetchEquipmentFailed`),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          console.error(`Failed to call API getEquipmentByProjectId. errors = `, result);
        }

        // return result?.data?.getEquipmentByProjectId;

      } catch (error) {
        console.error("データ取得失敗:", error);
        toast({
          title: TITLE,
          description: t(`${pb}.message.fetchEquipmentFailed`),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return null;
      };
    };
    getEquipmentByProjectId(projectId);
    getEquipmentByProjectId(pastProjectId);
    // 現PJのライン情報は非稼働時間の取得のみに使用していたため、取得不要になった
    // （過去ベスト月・勤務体制・稼働率はすべて手入力を正とする方針へ変更）
    fetchProductLine(pastProductionLineId);
  }, [isOpen, pastProjectId, pastProductionLineId, pastReviewDate])

  /**
 * 製造ライン情報取得
 * @param productionLineId 製造ラインID
 * @returns 製造ライン情報
 */
  const fetchProductLine = async (productionLineId: string) => {
    try {
      if (!productionLineId) {
        return;
      }

      // 過去ラインなら紐づけ時点の承認済み振り返り日（pastReviewDate）を使う（現PJラインは画面のreviewDate）
      const effectiveReviewDate = productionLineId === pastProductionLineId
        ? pastReviewDate
        : reviewDate;

      const payload = {
        query: `
          query getProductionLineById($projectId: String, $productionLineId: String, $reviewDate: String, $referencingProjectId: String) {
            getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
              productionLineId
              cycleTime
              yieldRate
            }
          }`,
        variables: {
          projectId: productionLineId === pastProductionLineId ? pastProjectId : projectId,
          productionLineId,
          ...(effectiveReviewDate && { reviewDate: effectiveReviewDate }),
          ...(productionLineId === pastProductionLineId && { referencingProjectId: projectId })
        }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);
      // 製造ライン情報取得設定

      // pastProductionLineIdなら（現在の呼び出し元は過去ラインのみ）
      if (productionLineId === pastProductionLineId) {
        const ProductionLineObj = response.data?.getProductionLineById;
        setPastProductionLine(ProductionLineObj);
      }

      // 製造ライン情報取得
      if (response?.data?.getProductionLineById?.productionLineId) {

      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t(`${pb}.message.fetchProductionLineFailed`),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProductionLineById. errors = `, response?.errors);
      }
    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('common.message.fetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }

  // 必要人数の合計を出す（ライン全体）
  const getTotalPeopleRequired = (equipments: EquipmentType[]): number => {
    return equipments.reduce(
      (sum, item) => sum + (item.numberOfPeopleRequiredForEquipment ?? 0),
      0
    );
  }

  /**
   *  稼働人数（IDの先頭部分で一致を判定）
   */
  const getNumberOfPeople = (list: EquipmentType[], id: string): number => {
    const target = list.find(item => item.equipmentId?.startsWith(id.split('-').slice(0, -1).join('-')));
    return target?.numberOfPeopleRequiredForEquipment ?? 0;
  };
  /**
   * 閉じるハンドラー
   */
  const handleClose = () => {
    // Mapを配列に変換して親に渡す
    const dataArray = Array.from(benchmarkMap.values());
    onUpdate(dataArray);
    onClose();
  };

  /**
   * 数値フォーマット
   */
  const formatNumber = (value: number | Decimal | undefined | null, currency?: string): string => {
    if (value === undefined || value === null) return "";
    const numValue = value instanceof Decimal ? value.toNumber() : value;
    return numValue.toLocaleString();
  };

  /**
   * 金額計算（設備単位）
   */
  const calcAmount = (unitPrice: number | undefined, quantity: number | undefined): Decimal => {
    if (!unitPrice || !quantity) return new Decimal(0);
    return new Decimal(unitPrice).mul(quantity);
  };

  /**
   *  増産分金額計算
   */
  const getAmountDifference = (
    equipment: EquipmentType,
    pastEquipments: EquipmentType[]
  ): Decimal => {
    // equipmentId の共通部分を抽出（例：A-1-2 → A-1）
    const baseId = equipment.equipmentId
      ?.split("-")
      .slice(0, -1)
      .join("-");

    // 過去データの中で、同じ接頭部分を持つものを探す
    const past = pastEquipments.find((p) =>
      p.equipmentId?.startsWith(baseId ?? "")
    );
    // 現在のデータ
    const currentAmount = calcAmount(equipment.unitPrice, equipment.quantity);

    // 過去データが存在しない場合 → 現在データをそのまま返す
    if (!past) {
      return currentAmount;
    }
    // 現在の個数ー過去の個数
    const calculateInvestmentDifference = calcAmount(equipment.unitPrice, (equipment.quantity ?? 0) - (past.quantity ?? 0));

    // 過去との個数の差を計算した投資金額を返す
    return calculateInvestmentDifference;
  };

  // ライン全体の増産分計算
  const calculateTotalInvestment = (equipments: EquipmentType[], pastEquipments: EquipmentType[]): Decimal => {
    return equipments.reduce((total, equipment) => {
      const amount = getAmountDifference(equipment, pastEquipments) ?? new Decimal(0);
      return total.add(amount);
    }, new Decimal(0));
  };

  // 固定選択肢のコードと多言語キーの対応
  const QUANTITY_UNIT_KEYS = [
    { code: "unit", key: "features.InvestmentScreening.Equipment.options.quantityUnit.unit" },
    { code: "set",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.set" },
    { code: "pcs",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.pcs" },
  ] as const;

  /**
   * 【出力用】DB保存値（unit/set/pcs または自由入力）をスプレッドシート表示名に変換
   */
  const toSpreadsheetUnitLabel = (code?: string | null): string => {
    const targetCode = code || "unit"; // 初期値
    const matched = QUANTITY_UNIT_KEYS.find(item => item.code === targetCode);
    // 固定選択肢に一致すれば翻訳テキスト（例: "台"）、なければ自由入力値をそのまま返す
    return matched ? t(matched.key) : targetCode;
  };
  
  /**
   * レンダリング: 設備入力モード
   */
  const renderEquipmentTable = () => {
    return (
      <TableContainer border="1px solid" borderColor="gray.200" height="100%" overflowY="unset" overflowX="auto">
        <Table variant="simple" style={{ tableLayout: "fixed" }}>
          <Thead position="sticky" top={0} zIndex={10} backgroundColor="gray.100" boxShadow={`0 -1px 0 #EEEEEE`}>
            <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="15%">{t(`${pb}.equipmentName`, '設備名')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="12%">{t(`${pb}.unitPrice`, '単価')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="8%">{t(`${pb}.currency`, '通貨')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="8%">{t(`${pb}.quantity`, '数量')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="8%">{t(`${pb}.quantityUniy`, '数量単位')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="13%">{t(`${pb}.investmentAmount`, '投資金額')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="12%">{t(`${pb}.bookValue`, '簿価')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="15%">{t(`${pb}.benchmarkInput`, 'ベンチマーク入力')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {equipmentData.map((equipment) => {
              const amount = getAmountDifference(equipment, pastEquipmentId);
              const existingBenchmark = benchmarkMap.get(equipment.equipmentId || "");

              return (
                <Tr key={equipment.equipmentId} _hover={{ bg: "gray.50" }}>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <Text isTruncated title={equipment.equipmentName || ""}>
                      {equipment.equipmentName}
                    </Text>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {formatNumber(equipment.unitPrice, equipment.currencyName ?? undefined)}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                    {equipment.currencyName}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {formatNumber(equipment.quantity)}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {toSpreadsheetUnitLabel(equipment.quantityUnit)}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {formatNumber(amount, equipment.currencyName ?? undefined)}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {formatNumber(new Decimal(equipment.bookValue || 0), equipment.currencyName ?? undefined)}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <BenchmarkInputPopover
                        inputType={inputType}
                        equipmentId={equipment.equipmentId ?? undefined}
                        equipmentName={equipment.equipmentName ?? undefined}
                        benchmarkData={existingBenchmark}
                        processCodeData={processCodeData}
                        isSave={isSaving}
                        onSave={handleSaveBenchmark}
                        pagePermissions={pagePermissions}
                        updateDate={projectAdditionalInfo?.updateDate}
                        totalNumberOfPeople={getNumberOfPeople(equipmentData, equipment?.equipmentId || "")}
                        pastTotalNumberOfPeople={getNumberOfPeople(pastEquipmentId, equipment?.equipmentId || "")}
                        pastUniqueProcess={pastUniqueProcess}
                        partNoData={partNoData}
                      />
                      <Icon
                        as={FaCheckCircle}
                        boxSize={8}
                        mr={2}
                        color={!existingBenchmark ? '#ccc' : 'green'}
                      />
                    </Box>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  /**
   * レンダリング: ライン入力モード
   */
  const renderLineTable = () => {
    const line = productionLineData && productionLineData.length > 0 ? productionLineData[0] : null;
    if (!line) {
      return <Text color="red.500">{t(`${pb}.lineInfoUnavailable`, 'ライン情報が取得できません。')}</Text>;
    }

    const existingBenchmark = benchmarkMap.get(line.productionLineId || "");

    return (
      <TableContainer border="1px solid" borderColor="gray.200" height="auto">
        <Table variant="simple" style={{ tableLayout: "fixed" }}>
          <Thead backgroundColor="gray.100">
            <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="27%">{t(`${pb}.lineName`, 'ライン名')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="8%">{t(`${pb}.currency`, '通貨')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="25%">{t(`${pb}.investmentAmount`, '投資金額')}</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="20%">{t(`${pb}.benchmarkInput`, 'ベンチマーク入力')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr _hover={{ bg: "gray.50" }}>
              <Td borderRight="1px solid" borderColor="gray.200">
                <Text isTruncated title={line.productionLineName || ""}>
                  {line.productionLineName}
                </Text>
              </Td>
              <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                {currencyName || ""}
              </Td>
              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                {formatNumber(calculateTotalInvestment(equipmentData, pastEquipmentId), currencyName)}
              </Td>
              <Td borderRight="1px solid" borderColor="gray.200">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <BenchmarkInputPopover
                    inputType={inputType}
                    productionLineId={line.productionLineId}
                    productionLineName={line.productionLineName}
                    benchmarkData={existingBenchmark}
                    processCodeData={processCodeData}
                    isSave={isSaving}
                    onSave={handleSaveBenchmark}
                    pagePermissions={pagePermissions}
                    updateDate={projectAdditionalInfo?.updateDate}
                    pastUniqueProcess={pastUniqueProcess}
                    productionLineData={productionLineData}
                    pastProductionLineData={pastProductionLine}
                    totalPeopleRequired={getTotalPeopleRequired(equipmentId) + getTotalPeopleRequired(pastEquipmentId)}
                    pastTotalPeopleRequired={getTotalPeopleRequired(pastEquipmentId)}
                    partNoData={partNoData}
                  />
                  <Icon
                    as={FaCheckCircle}
                    boxSize={8}
                    mr={2}
                    color={!existingBenchmark ? '#ccc' : 'green'}
                  />
                </Box>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  /**
   * 売上インパクトデータを取得（生産数比較 + 増産による売上・粗利増分）
   */
  const getSalesImpactData = () => {
    const rows: {
      equipmentName: string;
      partNo: string;
      actualBestVolume: number;
      investmentPlanVolume: number;
      volumeDiff: number;
      unitPrice: number;
      materialCostPerUnit: number;
      salesDiff: number;       // 月次売上増分 = volumeDiff × unitPrice
      grossProfitDiff: number; // 月次粗利増分 = volumeDiff × (unitPrice - materialCostPerUnit)
    }[] = [];

    // ライン名: benchmark（r_ceas_project_additional_infoのbenchmark_data）からの参照を廃止し、productionLineData（M_CEAS_PRODUCTION_LINE）から製造ライン名を参照するように変更
    const getLineName = () => {
      if (productionLineData) {
        const line = productionLineData && productionLineData.length > 0 ? productionLineData[0] : null;
        if (line?.productionLineName) return line.productionLineName;
      }
      return "";
    };

    benchmarkMap.forEach((benchmark) => {
      if (benchmark.inputType !== inputType) return;

      if (benchmark.benchmarkByPartNo) {
        // PartNoモード: 全PartNoを展開
        Object.entries(benchmark.benchmarkByPartNo).forEach(([partNo, data]) => {
          const actualVolume = data.actualBest?.productionVolume || 0;
          const planVolume = data.investmentPlan?.productionVolume || 0;
          const volumeDiff = planVolume - actualVolume;
          const financial = partNoFinancialMap.get(partNo);
          const unitPrice = financial?.unitPrice || 0;
          const materialCostPerUnit = financial?.materialCostPerUnit || 0;
          rows.push({
            equipmentName: benchmark.equipmentName || getLineName(),
            partNo,
            actualBestVolume: actualVolume,
            investmentPlanVolume: planVolume,
            volumeDiff,
            unitPrice,
            materialCostPerUnit,
            salesDiff: new Decimal(volumeDiff).times(unitPrice).toNumber(),
            grossProfitDiff: new Decimal(volumeDiff).times(new Decimal(unitPrice).minus(materialCostPerUnit)).toNumber(),
          });
        });
      } else {
        // ライン入力モード: PartNo別データがない場合
        const actualVolume = benchmark.actualBest?.productionVolume || 0;
        const planVolume = benchmark.investmentPlan?.productionVolume || 0;
        const volumeDiff = planVolume - actualVolume;
        const equipmentName = benchmark.equipmentName || getLineName();

        // partNoFinancialMapからPartNo数を判定
        const availablePartNos = Array.from(partNoFinancialMap.keys());

        if (availablePartNos.length === 1) {
          // PartNo 1つ: そのPartNoの財務データを使用
          const partNo = availablePartNos[0];
          const financial = partNoFinancialMap.get(partNo);
          const unitPrice = financial?.unitPrice || 0;
          const materialCostPerUnit = financial?.materialCostPerUnit || 0;
          rows.push({
            equipmentName,
            partNo,
            actualBestVolume: actualVolume,
            investmentPlanVolume: planVolume,
            volumeDiff,
            unitPrice,
            materialCostPerUnit,
            salesDiff: new Decimal(volumeDiff).times(unitPrice).toNumber(),
            grossProfitDiff: new Decimal(volumeDiff).times(new Decimal(unitPrice).minus(materialCostPerUnit)).toNumber(),
          });
        } else if (inputType === "line" && availablePartNos.length >= 2) {
          // ライン入力×複数PartNo: 数量単価の販売数量比率で按分
          const totalQuantity = availablePartNos.reduce((sum, pn) => {
            const financial = partNoFinancialMap.get(pn);
            return sum + (financial?.quantity || 0);
          }, 0);

          if (totalQuantity > 0) {
            let remainingActual = actualVolume;
            let remainingPlan = planVolume;
            availablePartNos.forEach((partNo, idx) => {
              const financial = partNoFinancialMap.get(partNo);
              const quantity = financial?.quantity || 0;
              const isLast = idx === availablePartNos.length - 1;
              // 最後のPartNoは残りを割り当て（端数ずれ防止）
              const partNoActualVolume = isLast ? remainingActual : Math.round(actualVolume * quantity / totalQuantity);
              const partNoPlanVolume = isLast ? remainingPlan : Math.round(planVolume * quantity / totalQuantity);
              remainingActual -= partNoActualVolume;
              remainingPlan -= partNoPlanVolume;
              const partNoVolumeDiff = partNoPlanVolume - partNoActualVolume;
              const unitPrice = financial?.unitPrice || 0;
              const materialCostPerUnit = financial?.materialCostPerUnit || 0;

              rows.push({
                equipmentName,
                partNo,
                actualBestVolume: partNoActualVolume,
                investmentPlanVolume: partNoPlanVolume,
                volumeDiff: partNoVolumeDiff,
                unitPrice,
                materialCostPerUnit,
                salesDiff: new Decimal(partNoVolumeDiff).times(unitPrice).toNumber(),
                grossProfitDiff: new Decimal(partNoVolumeDiff).times(new Decimal(unitPrice).minus(materialCostPerUnit)).toNumber(),
              });
            });
          } else {
            // 数量データなし: フォールバック
            rows.push({
              equipmentName,
              partNo: "-",
              actualBestVolume: actualVolume,
              investmentPlanVolume: planVolume,
              volumeDiff,
              unitPrice: 0,
              materialCostPerUnit: 0,
              salesDiff: 0,
              grossProfitDiff: 0,
            });
          }
        } else {
          // PartNo 0または設備入力で複数: 財務データなし（生産数比較のみ）
          rows.push({
            equipmentName,
            partNo: "-",
            actualBestVolume: actualVolume,
            investmentPlanVolume: planVolume,
            volumeDiff,
            unitPrice: 0,
            materialCostPerUnit: 0,
            salesDiff: 0,
            grossProfitDiff: 0,
          });
        }
      }
    });

    return rows;
  };

  /**
   * 売上インパクトセクションのレンダリング
   */
  const renderSalesImpact = () => {
    const rows = getSalesImpactData();

    if (rows.length === 0) {
      return (
        <Box px="10px" py={4}>
          <Text color="gray.500">{t(`${pb}.enterBenchmarkData`, 'ベンチマークデータを入力してください。')}</Text>
        </Box>
      );
    }

    const totalVolumeDiff = rows.reduce((sum, r) => sum + r.volumeDiff, 0);
    const totalSalesDiff = rows.reduce((sum, r) => new Decimal(sum).plus(r.salesDiff).toNumber(), 0);
    const totalGrossProfitDiff = rows.reduce((sum, r) => new Decimal(sum).plus(r.grossProfitDiff).toNumber(), 0);
    const nameLabel = inputType === "equipment"
      ? t(`${pb}.equipmentName`, '設備名')
      : t(`${pb}.lineName`, 'ライン名');
    const hasFinancialData = rows.some(r => r.unitPrice > 0);
    // 増産による売上・粗利セクションが非表示の場合のみ、PartNo別入力を促す
    // ライン入力モードではPartNo別入力ができないため案内不要
    const showPartNoGuidance = inputType === "equipment" && !hasFinancialData && rows.length > 0;
    // ライン入力×複数PartNo按分時の注釈表示判定
    const isLineProrated = inputType === "line" && rows.length >= 2 && rows.some(r => r.partNo !== "-");

    const borderStyle = "1px solid";
    const borderColor = "gray.200";

    return (
      <>
        <Box px="10px" h="calc(90vh - 320px)" overflowY="auto">
          {/* 生産数比較テーブル */}
          <Text fontWeight="bold" mb={2}>{t(`${pb}.volumeComparison`, '生産数比較')}</Text>
          <TableContainer border={borderStyle} borderColor={borderColor} mb={4}>
            <Table variant="simple" size="sm" style={{ tableLayout: "fixed" }}>
              <Thead bg="gray.100">
                <Tr>
                  <Th borderRight={borderStyle} borderColor={borderColor} w="22%">{nameLabel}</Th>
                  <Th borderRight={borderStyle} borderColor={borderColor} w="16%">PartNo</Th>
                  <Th borderRight={borderStyle} borderColor={borderColor} w="18%" textAlign="right">{t(`${pb}.actualBestVolume`, '実績ベスト (個/月)')}</Th>
                  <Th borderRight={borderStyle} borderColor={borderColor} w="18%" textAlign="right">{t(`${pb}.investmentPlanVolume`, '投資計画値 (個/月)')}</Th>
                  <Th w="18%" textAlign="right">{t(`${pb}.volumeDiff`, '生産数増分 (個/月)')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row, idx) => (
                  <Tr key={`vol-${row.equipmentName}-${row.partNo}-${idx}`} _hover={{ bg: "gray.50" }}>
                    <Td borderRight={borderStyle} borderColor={borderColor}>{row.equipmentName}</Td>
                    <Td borderRight={borderStyle} borderColor={borderColor}>{row.partNo}</Td>
                    <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right">{formatNumber(row.actualBestVolume)}</Td>
                    <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right">{formatNumber(row.investmentPlanVolume)}</Td>
                    <Td textAlign="right" color={row.volumeDiff > 0 ? "green.600" : row.volumeDiff < 0 ? "red.600" : undefined}>
                      {row.volumeDiff > 0 ? "+" : ""}{formatNumber(row.volumeDiff)}
                    </Td>
                  </Tr>
                ))}
                <Tr bg="gray.50" fontWeight="bold">
                  <Td borderRight={borderStyle} borderColor={borderColor}>{t(`${pb}.total`, '合計')}</Td>
                  <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                  <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                  <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                  <Td textAlign="right" color={totalVolumeDiff > 0 ? "green.600" : totalVolumeDiff < 0 ? "red.600" : undefined}>
                    {totalVolumeDiff > 0 ? "+" : ""}{formatNumber(totalVolumeDiff)}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>

          {/* 財務インパクトテーブル */}
          {hasFinancialData && (
            <>
              <Text fontWeight="bold" mb={2}>{t(`${pb}.salesGrossProfitIncrease`, '増産による売上・粗利増分')}</Text>
              <TableContainer border={borderStyle} borderColor={borderColor} mb={4}>
                <Table variant="simple" size="sm" style={{ tableLayout: "fixed" }}>
                  <Thead bg="gray.100">
                    <Tr>
                      <Th borderRight={borderStyle} borderColor={borderColor} w="18%">{nameLabel}</Th>
                      <Th borderRight={borderStyle} borderColor={borderColor} w="14%">PartNo</Th>
                      <Th borderRight={borderStyle} borderColor={borderColor} w="12%" textAlign="right">{t(`${pb}.unitPrice`, '単価')}</Th>
                      <Th borderRight={borderStyle} borderColor={borderColor} w="12%" textAlign="right">{t(`${pb}.materialCostPerUnit`, '材料費/個')}</Th>
                      <Th borderRight={borderStyle} borderColor={borderColor} w="12%" textAlign="right">{t(`${pb}.volumeDiffShort`, '増分 (個/月)')}</Th>
                      <Th borderRight={borderStyle} borderColor={borderColor} w="14%" textAlign="right">{t(`${pb}.salesDiff`, '売上増分')}</Th>
                      <Th w="14%" textAlign="right">{t(`${pb}.grossProfitDiff`, '粗利増分')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.filter(r => r.unitPrice > 0).map((row, idx) => (
                      <Tr key={`fin-${row.equipmentName}-${row.partNo}-${idx}`} _hover={{ bg: "gray.50" }}>
                        <Td borderRight={borderStyle} borderColor={borderColor}>{row.equipmentName}</Td>
                        <Td borderRight={borderStyle} borderColor={borderColor}>{row.partNo}</Td>
                        <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right">{formatNumber(row.unitPrice)}</Td>
                        <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right">{formatNumber(row.materialCostPerUnit)}</Td>
                        <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right">
                          {row.volumeDiff > 0 ? "+" : ""}{formatNumber(row.volumeDiff)}
                        </Td>
                        <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right" color={row.salesDiff > 0 ? "green.600" : row.salesDiff < 0 ? "red.600" : undefined}>
                          {row.salesDiff > 0 ? "+" : ""}{formatNumber(Math.round(row.salesDiff))}
                        </Td>
                        <Td textAlign="right" color={row.grossProfitDiff > 0 ? "green.600" : row.grossProfitDiff < 0 ? "red.600" : undefined}>
                          {row.grossProfitDiff > 0 ? "+" : ""}{formatNumber(Math.round(row.grossProfitDiff))}
                        </Td>
                      </Tr>
                    ))}
                    <Tr bg="gray.50" fontWeight="bold">
                      <Td borderRight={borderStyle} borderColor={borderColor}>{t(`${pb}.total`, '合計')}</Td>
                      <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                      <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                      <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                      <Td borderRight={borderStyle} borderColor={borderColor}></Td>
                      <Td borderRight={borderStyle} borderColor={borderColor} textAlign="right" color={totalSalesDiff > 0 ? "green.600" : totalSalesDiff < 0 ? "red.600" : undefined}>
                        {totalSalesDiff > 0 ? "+" : ""}{formatNumber(Math.round(totalSalesDiff))}
                      </Td>
                      <Td textAlign="right" color={totalGrossProfitDiff > 0 ? "green.600" : totalGrossProfitDiff < 0 ? "red.600" : undefined}>
                        {totalGrossProfitDiff > 0 ? "+" : ""}{formatNumber(Math.round(totalGrossProfitDiff))}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </>
          )}

        </Box>
        {/* 注釈（スクロール領域の外に固定表示） */}
        <Box px="10px" pt={1}>
          {showPartNoGuidance && (
            <Text fontSize="sm" color="blue.600">
              {t(`${pb}.partNoGuidance`, '※ PartNo別にベンチマークを入力すると、売上・粗利の増分も表示されます。')}
            </Text>
          )}
          {isLineProrated && (
            <Text fontSize="xs" color="orange.600">
              {t(`${pb}.lineProratedNote`, '※ ライン全体の生産数を、各PartNoの販売数量合計の比率で按分しています。')}
            </Text>
          )}
          {hasFinancialData && (
            <Text fontSize="xs" color="gray.500">
              {t(`${pb}.estimateNote1`, '※ 最大生産能力の増分に基づく試算値です。実際の生産計画・販売数量とは異なる場合があります。')}
              <br />
              {t(`${pb}.estimateNote2`, '※ 単価は販売数量・販売単価の加重平均を使用。粗利 = (単価 - 材料費) × 生産数増分。労務費・間接費は含みません。')}
            </Text>
          )}
        </Box>
      </>
    );
  };

  /**
   * レンダリング
   */
  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="1783px" minW="720px">
        <ModalHeader>
          <HStack spacing={4}>
            <Text>{t(`${pb}.title`, '過去ベストベンチマーク')}</Text>
            <RadioGroup value={inputType} onChange={(value) => setInputType(value as "equipment" | "line")}>
              <HStack spacing={4}>
                <Box width="30px" mr="5px">
                  <Popover placement="top-end">
                    <PopoverTrigger>
                      {/* ？アイコン */}
                      <button style={{ background: 'none', border: 'none', padding: 0 }}>
                        <FaRegQuestionCircle size="30px" cursor="pointer" />
                      </button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent width="450px" bgColor="#fffff0">
                        <PopoverArrow />
                        <PopoverCloseButton top="8px" right="8px" />
                        <PopoverBody pr="30px">
                          <VStack align="start" spacing={3} mb="30px" mt="15px">
                            <Text>
                              {t(`${pb}.equipmentInputHelp`, '設備入力：設備増設')}
                            </Text>
                            <Text>
                              {t(`${pb}.lineInputHelp`, 'ライン全体：ライン新設')}
                            </Text>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </Popover>
                </Box>
                <Radio value="equipment">{t(`${pb}.equipmentExpansion`, '設備増設')}</Radio>
                <Radio value="line">{t(`${pb}.lineWhole`, 'ライン全体')}</Radio>
              </HStack>
            </RadioGroup>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Tabs>
            <TabList>
              <Tab>{t(`${pb}.listTab`, '一覧')}</Tab>
              <Tab>{t(`${pb}.salesImpactTab`, '売上インパクト')}</Tab>
            </TabList>
            <TabPanels>
              {/* 一覧タブ */}
              <TabPanel px={0}>
                {inputType === "equipment" ? (
                  <>
                    <Text display="flex" justifyContent="flex-start" ml={5} mb={2}>
                      {t(`${pb}.equipmentCount`, '設備数: {{count}} 件', { count: equipmentData.length ?? 0 })}
                    </Text>
                    <Box display="flex" justifyContent="flex-start" h="calc(90vh - 320px)" mt="10px" px="10px">
                      <Box width="100%" overflowX="auto">
                        {renderEquipmentTable()}
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Box display="flex" justifyContent="flex-start" px="10px">
                    <Box width="100%" overflowX="auto">
                      {renderLineTable()}
                    </Box>
                  </Box>
                )}
              </TabPanel>
              {/* 売上インパクトタブ */}
              <TabPanel px={0}>
                {renderSalesImpact()}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} m={1}>
            {t('common.close', '閉じる')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProductionCapacityBenchmarkModal;
